import styles from './StarRating.module.css';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  size?: 'sm' | 'md' | 'lg';
  readOnly?: boolean;
  className?: string;
  ariaLabel?: string;
}

function clamp(value: number): number {
  return Math.max(0, Math.min(5, value));
}

export function StarRating({
  value,
  onChange,
  size = 'md',
  readOnly = true,
  className,
  ariaLabel,
}: StarRatingProps): JSX.Element {
  const safeValue = clamp(value);

  return (
    <div
      className={[
        styles.rating,
        styles[size],
        readOnly ? styles.readOnly : styles.interactive,
        className ?? '',
      ].filter(Boolean).join(' ')}
      aria-label={ariaLabel}
    >
      {Array.from({ length: 5 }, (_, index) => {
        const starNumber = index + 1;
        const fill = Math.max(0, Math.min(1, safeValue - index));
        const pct = `${fill * 100}%`;

        if (readOnly) {
          return (
            <span key={starNumber} className={styles.star} aria-hidden="true">
              <span className={styles.starBase}>★</span>
              <span className={styles.starFill} style={{ width: pct }}>★</span>
            </span>
          );
        }

        return (
          <button
            key={starNumber}
            type="button"
            className={styles.starButton}
            onClick={() => onChange?.(starNumber)}
            aria-label={`Rate ${starNumber} out of 5`}
          >
            <span className={styles.star} aria-hidden="true">
              <span className={styles.starBase}>★</span>
              <span className={styles.starFill} style={{ width: pct }}>★</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
